// src/components/pages/KeyAuthPage/KeyAuthPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiService } from '../../services/api_url';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import './KeyAuthPage.scss';

type AuthStatus = 'loading' | 'success' | 'error';

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const KeyAuthPage: React.FC = () => {
    const { url_key } = useParams<{ url_key: string }>();
    const navigate = useNavigate();

    const [status, setStatus] = useState<AuthStatus>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [countdown, setCountdown] = useState<number>(5);

    useEffect(() => {
        if (!url_key) {
            setStatus('error');
            setErrorMessage('Недействительная ссылка: ключ отсутствует');
            return;
        }

        let intervalId: NodeJS.Timeout | null = null;
        let mounted = true;

        const authenticateWithKey = async (): Promise<void> => {
            try {
                setStatus('loading');

                const response = await apiService.authenticateWithKey(url_key);

                if (!response?.success) {
                    throw new Error('Не удалось подтвердить ключ');
                }

                // Даём браузеру применить Set-Cookie и делаем одну нормальную проверку авторизации.
                await wait(250);
                const authCheck = await apiService.checkAuth({ force: true });

                if (!mounted) return;

                if (authCheck.status !== 'authenticated') {
                    if (authCheck.status === 'error') {
                        throw new Error(authCheck.message || 'Не удалось проверить авторизацию');
                    }

                    throw new Error('Не удалось подтвердить авторизацию');
                }

                setStatus('success');
                setCountdown(5);

                intervalId = setInterval(() => {
                    setCountdown((prev) => {
                        if (prev <= 1) {
                            if (intervalId) clearInterval(intervalId);
                            navigate('/');
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } catch (error: unknown) {
                if (!mounted) return;

                setStatus('error');

                if (axios.isAxiosError(error)) {
                    if (error.code === 'ERR_NETWORK') {
                        setErrorMessage('Ошибка сети. Проверьте подключение к интернету.');
                    } else if (error.response?.status === 404) {
                        setErrorMessage('Пользователь с таким ключом не найден');
                    } else if (error.response?.status === 500) {
                        setErrorMessage('Внутренняя ошибка сервера');
                    } else {
                        setErrorMessage(error.response?.data?.detail || error.message || 'Ошибка при подключении к серверу');
                    }
                } else if (error instanceof Error) {
                    setErrorMessage(error.message);
                } else {
                    setErrorMessage('Неизвестная ошибка');
                }
            }
        };

        authenticateWithKey();

        return () => {
            mounted = false;
            if (intervalId) clearInterval(intervalId);
        };
    }, [url_key, navigate]);

    const handleGoToBot = (): void => {
        window.location.href = 'https://t.me/GhostOfTheInternetBot';
    };

    const handleRetry = (): void => {
        window.location.reload();
    };

    if (status === 'loading') {
        return (
            <div className="key-auth-page-wrapper">
                <BackgroundCanvasWithoutRadar />
                <div className="key-auth-content">
                    <div className="key-auth-loading-container">
                        <div className="key-auth-loader-spinner"></div>
                        <h2 className="key-auth-title">Проверка ключа доступа...</h2>
                        <p className="key-auth-subtitle">Пожалуйста, подождите</p>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="key-auth-page-wrapper">
                <BackgroundCanvasWithoutRadar />
                <div className="key-auth-content">
                    <div className="key-auth-success-container">
                        <div className="key-auth-success-icon">✓</div>
                        <h2 className="key-auth-title">Успешная авторизация!</h2>
                        <p className="key-auth-subtitle">
                            Вы будете перенаправлены в приложение через{' '}
                            <span className="key-auth-countdown">{countdown}</span> сек
                        </p>
                        {countdown === 0 && (
                            <p className="key-auth-redirect-hint">Перенаправление...</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="key-auth-page-wrapper">
            <BackgroundCanvasWithoutRadar />
            <div className="key-auth-content">
                <div className="key-auth-error-container">
                    <div className="key-auth-error-icon">✕</div>
                    <h2 className="key-auth-title">Ошибка входа</h2>
                    <p className="key-auth-error-message">{errorMessage || 'Неизвестная ошибка'}</p>
                    <p className="key-auth-hint">
                        Проверьте ссылку или запросите новую в боте
                    </p>
                    <div className="key-auth-button-group">
                        <button
                            className="key-auth-button key-auth-button-primary"
                            onClick={handleGoToBot}
                        >
                            Перейти в бота
                        </button>
                        <button
                            className="key-auth-button key-auth-button-secondary"
                            onClick={handleRetry}
                        >
                            Попробовать снова
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KeyAuthPage;
